import { Component } from 'react'
import { Asn, DecodedItem, ShippingParcel, Shippings, ShippingTag, ShippingSelectData } from 'stylewhere/api'
import {
  FullLoadingLayer,
  OperationStart,
  Page,
  ShippingStartInput,
  ShippingStartListAndInput
} from 'stylewhere/components'
import { ShippingExtensions } from 'stylewhere/extensions'
import { T, __ } from 'stylewhere/i18n'
import type { Routes } from 'stylewhere/pages'
import {
  FormSchemaData,
  getDataFromSchema,
  OperationReadingProvider,
  OperationReadingState,
  RemoteOperation,
  RfidReader,
  Router,
  ShippingOperationConfig,
  AppStore,
  Sounds,
  FormSchema
} from 'stylewhere/shared'
import { CustomError } from 'stylewhere/shared/errors'
import { PackingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  askUserConfirmation,
  canDeleteParcel,
  getDefaultFilterShipment,
  isModalError,
  showToastError
} from 'stylewhere/utils'
import { ShippingParcelStartParams } from './ShippingParcelStart'

export interface ShippingStartParams {
  opCode: string
}

export interface ShippingStartState {
  skipAsnSelection?: boolean
  items: DecodedItem<string>[]
  renderIteration: number
  loading: boolean
}

interface ShippingStartProps {
  match: { params: ShippingStartParams }
  location: { state?: OperationReadingState }
}

export default class ShippingStart extends Component<ShippingStartProps, ShippingStartState> {
  submitPath: Routes = '/shipping/:opCode/reading'
  asnParcelPath: Routes = '/shipping/:opCode/asn/:asnId'

  get operation() {
    return RemoteOperation.getOperationConfig<ShippingOperationConfig | PackingOperationConfig>(
      this.props.match.params.opCode
    )
  }
  checkingIdentifier = false
  isModal = false

  state: ShippingStartState = {
    items: [],
    renderIteration: 0,
    loading: false
  }

  get locationState(): OperationReadingState | undefined {
    return this.props.location.state
  }

  tags: ShippingTag[] = []

  mustSelect() {
    return (
      this.operation &&
      ['INBOUND', 'OUTBOUND'].includes(this.operation.type) &&
      ((this.operation.shippingMode === 'parcelByParcel' &&
        (this.operation.showListOnStart || this.operation.canCreateParcels)) ||
        (this.operation.shippingMode === 'shipment' &&
          (this.operation.showListOnStart || this.operation.canCreateShipments))) &&
      !this.state.skipAsnSelection
    )
  }

  setSkipAsnSelection = () => {
    this.setState({ skipAsnSelection: false })
  }

  isParcelByParcel = () => {
    return this.operation.shippingMode === 'parcelByParcel'
  }

  isAsnSelectMode() {
    return this.operation.showListOnStart || this.operation.canCreateShipments || this.operation.canCreateParcels
  }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    this.isModal = isModalError(this.operation)
    if (this.operation.startWithRfidSearch) {
      RfidReader.setBatchInterval(this.operation.batchInterval)
      RfidReader.setBatchIntervalTagCount(this.operation.batchIntervalTagCount)
      RfidReader.setBatchIntervalTime(this.operation.shippingReadTimeout ?? this.operation.batchIntervalTime)
      RfidReader.setAutomaticStop(this.operation.autostopAntennaTimeout > 0)
      RfidReader.setAutomaticStopTime(this.operation.autostopAntennaTimeout)
      await OperationReadingProvider.init(
        this.operation,
        this.locationState,
        this.goBack,
        this.setRfidReaderDecode,
        true
      )
    }
  }

  goBack = () => {
    if (this.isAsnSelectMode()) {
      this.setSkipAsnSelection()
    } else {
      Router.navigate('/')
    }
  }

  setRfidReaderDecode = () => {
    RfidReader.setDecodeFunction(async (epcs) => {
      return epcs.reduce((acc, epc) => {
        acc[epc] = { epc }
        return acc
      }, {})
    })
    RfidReader.setOnDecodedItemCallback(this.onDecodedItemCallback, this.getDecodeRequest())
  }

  getDecodeRequest = () => {
    return {
      url: Shippings.batchValidateEndpoint(this.operation),
      payload: {
        operationId: this.operation.id
      }
    }
  }

  parcelByIdentifierError = () => {
    const tagsToRemove = this.tags.map((tag) => tag.code)
    RfidReader.removeTags(tagsToRemove)
    this.tags = []
    this.checkingIdentifier = false
    this.setState({ loading: false })
  }

  onDecodedItemCallback = async (itemMapFromReader: { [tagCode: string]: DecodedItem }) => {
    if (this.checkingIdentifier) return
    this.checkingIdentifier = true
    RfidReader.stop()
    RfidReader.clear()
    this.call(itemMapFromReader)
  }

  call = async (itemMapFromReader) => {
    this.setState({ loading: true })
    let parcelCode
    const tags = Object.keys(itemMapFromReader).map((code) => ({ epc: code }))
    this.tags = [...this.tags, ...tags.map((t) => ({ code: t.epc, tid: '' }))]

    let parcelByIdentifier: ShippingParcel | undefined = undefined
    try {
      parcelByIdentifier = await Shippings.parcelByIdentifier(this.operation, this.tags)
    } catch (error) {
      this.parcelByIdentifierError()
      showToastError(error, __(T.error.error), this.isModal)
      this.checkingIdentifier = false
      return
    }

    let payload: any = undefined
    if (parcelByIdentifier?.code) {
      parcelCode = parcelByIdentifier.code || ''
      payload = {
        parcelCode,
        attributes: parcelByIdentifier.attributes || {},
        operationId: this.operation.id,
        asn: parcelByIdentifier.asn || {}
      }
    }

    try {
      if (parcelByIdentifier?.code) {
        const parcel = await Shippings.startParcel(this.operation, payload)
        const formData: Record<string, unknown> = {}
        const state: Record<string, any> = {
          formData,
          parcel,
          asn: parcelByIdentifier.asn,
          tags: this.tags.map((t) => ({ epc: t.code }))
        }

        if (parcelByIdentifier?.code && this.operation.formSchema?.some((s) => s.name === 'parcelCode')) {
          state.formData.parcelCode = parcelByIdentifier.code
        }
        this.setState({ loading: false })
        Router.navigate(this.submitPath, { opCode: this.operation.code }, { state })
      } else {
        this.parcelByIdentifierError()
        showToastError(__(T.error.no_parcel_found_by_identifier), __(T.error.search_error), this.isModal)
      }
    } catch (error) {
      this.parcelByIdentifierError()
      this.checkingIdentifier = false
      if (
        await ShippingExtensions.checkPackingUnpack(
          error as any,
          parcelCode,
          this.operation,
          () => this.onDecodedItemCallback(itemMapFromReader),
          () =>
            this.setState((prevState) => ({
              renderIteration: prevState.renderIteration + 1
            }))
        )
      )
        return
      if (error instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(error.message, payload)}. [${error.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      showToastError(error, __(T.error.error), this.isModal)
    }
    this.checkingIdentifier = false
  }

  onSelectParcel = async (selected: ShippingSelectData) => {
    const selectedParcel = selected as ShippingParcel
    if (['RECEIVED', 'READY_TO_RECEIVE', 'FORCE_RECEIVED', 'READY_TO_SEND', 'PACKED'].includes(selectedParcel.status)) {
      Router.navigate(
        '/shipping/:opCode/parcel/:parcelId',
        {
          opCode: this.operation.code,
          parcelId: selectedParcel.id
        },
        { state: { filterParams: Router.getQueryParams() } }
      )
    } else {
      const schemaParcel = ShippingExtensions.formSchema(this.operation, undefined, ['', 'parcel'])

      const formattedData = await Shippings.getInitialValueParcel(schemaParcel, selectedParcel.asn, selectedParcel)
      // override payload to send direct id for place if present
      const payloadStartParcel = formattedData.asn
        ? {
            ...getDataFromSchema(formattedData, schemaParcel),
            asn: {
              code: formattedData.asn.code,
              destinationPlaceId: formattedData.asn.destinationPlaceId?.id,
              originPlaceId: formattedData.asn.originPlaceId?.id
            }
          }
        : formattedData

      const operation = { ...this.operation }
      if (selectedParcel.status === 'INBOUND_ERROR') {
        operation.type = 'INBOUND'
      } else if (selectedParcel.status === 'OUTBOUND_ERROR') {
        operation.type = 'OUTBOUND'
      }

      try {
        const parcel = await Shippings.startParcel(operation, payloadStartParcel, operation.startParcelWithAsn)
        Router.navigate(
          this.submitPath,
          { opCode: operation.code },
          { state: { formData: formattedData, parcel, filterParams: Router.getQueryParams() } }
        )
      } catch (err) {
        showToastError(err, 'Start Parcel Error', this.isModal)
      }
    }
  }

  onSubmit = async (formData: FormSchemaData, operation: ShippingOperationConfig, schema: FormSchema) => {
    let parcelCode
    const payloadStartParcel: any = getDataFromSchema(formData, schema)
    try {
      parcelCode = payloadStartParcel.parcelCode
      const parcel = await Shippings.startParcel(operation, payloadStartParcel, operation.startParcelWithAsn)
      // set formData.parcelCode as parcel.code if not exist or if is empty
      if ((!formData.parcelCode || formData.parcelCode === '') && parcel.code) formData.parcelCode = parcel.code
      Router.navigate(
        this.submitPath,
        { opCode: operation.code },
        {
          state: {
            formData,
            parcel,
            filterParams: this.operation.shippingMode === 'parcelByParcel' ? { tab: 'parcel' } : {}
          }
        }
      )
    } catch (err) {
      if (
        await ShippingExtensions.checkPackingUnpack(
          err as any,
          parcelCode,
          this.operation,
          () => this.onSubmit(formData, operation, schema),
          () =>
            this.setState((prevState) => ({
              renderIteration: prevState.renderIteration + 1
            }))
        )
      )
        return
      if (err instanceof CustomError) {
        showToastError(
          `${CustomError.interpolateMessage(err.message, payloadStartParcel)}. [${err.errorCode}]`,
          __(T.error.error),
          this.isModal
        )
        return
      }
      showToastError(err, 'Start Parcel Error', this.isModal)
    }
  }

  navigateToAsn = (asn: Asn, newAsn?: boolean) => {
    const nextRouteParams: ShippingParcelStartParams = {
      opCode: this.operation.code,
      asnId: asn.id
    }
    Router.navigate(this.asnParcelPath, nextRouteParams, {
      state: {
        asn,
        filterParams:
          this.operation.shippingMode === 'parcelByParcel'
            ? newAsn
              ? { tab: 'shipment' }
              : Router.getQueryParams()
            : {}
      }
    })
  }

  onSelectAsnError = async (err: unknown, _selected: ShippingSelectData) => {
    showToastError(err, __(T.error.submit_asn), this.isModal)
  }

  onSelectAsn = async (selected: ShippingSelectData) => {
    try {
      const asn = selected as Asn
      const directAsn = asn.status && Shippings.getStartAsnStatus(this.operation).includes(asn.status)
      if (!directAsn || this.operation.shippingMode === 'parcelByParcel') {
        this.navigateToAsn(asn)
      } else if (asn?.id) {
        const formData = {
          shipmentCode: asn.code,
          originPlaceId:
            asn.originPlaceId && asn.originPlaceId !== null ? asn.originPlaceId : AppStore.defaultWorkstation?.placeId,
          destinationPlaceId:
            asn.destinationPlaceId && asn.destinationPlaceId !== null
              ? asn.destinationPlaceId
              : AppStore.defaultWorkstation?.placeId,
          attributes: asn.attributes ?? {}
        }
        const newAsn = await Shippings.startAsn(this.operation, formData)
        this.navigateToAsn(newAsn)
      } else {
        throw new Error(__(T.error.submit_asn))
      }
    } catch (err) {
      await this.onSelectAsnError(err, selected)
    }
  }

  onCreateAsn = async (formData: FormSchemaData, operation: ShippingOperationConfig, schema: FormSchema) => {
    try {
      if (!formData) throw new Error(__(T.error.submit_asn))
      // si normalizzano i dati del form per utilizzare anche eventuali confirmValuePath presenti nel FormSchema
      const payloadStartAsn = getDataFromSchema(formData, schema)
      const newAsn = await Shippings.startAsn(operation, payloadStartAsn)
      const asnId = newAsn.id
      if (asnId) {
        this.navigateToAsn(newAsn, true)
      } else {
        throw new Error(__(T.error.submit_asn))
      }
    } catch (err) {
      showToastError(err, __(T.error.submit_asn), this.isModal)
    }
  }

  onReadParcel = () => {
    this.setState({ skipAsnSelection: true })
  }

  canDeleteOrClear = (data: ShippingSelectData, shipment: boolean) => {
    if (!data || !this.operation.canDeleteParcel) return false
    if (!shipment) {
      return canDeleteParcel(data as ShippingParcel, this.operation)
    }
    return ['IN_OUTBOUND', 'DRAFT', 'IN_INBOUND'].includes(data.status)
  }

  handleDelete = async (data: ShippingSelectData, shipment: boolean) => {
    const confirmed = await askUserConfirmation(
      shipment ? __(T.title.delete_shipment_title) : __(T.custom.deleteSession),
      shipment ? __(T.subtitle.delete_shipment_message) : __(T.custom.deleteSessionMessage)
    )
    if (confirmed) {
      const result = await Shippings._deleteEntity(data.id, shipment ? 'shipment' : 'parcel', this.operation)
      if (result.success) {
        Sounds.success()
        this.setState((prevState) => ({
          renderIteration: prevState.renderIteration + 1
        }))
      } else {
        showToastError(result.message, __(T.misc.error), this.isModal)
      }
    }
  }

  onFiltersChange = (filters: any) => {
    Router.setQueryParams(filters)
  }

  getFilterParams = () => {
    if (this.locationState?.filterParams) {
      return this.locationState?.filterParams
    }
    return Router.getQueryParams()
  }

  public render() {
    const isParcel = this.isParcelByParcel()
    if (this.mustSelect()) {
      if (this.operation.showListOnStart) {
        const defaultFilter = getDefaultFilterShipment(this.operation, this.getFilterParams(), {})
        return (
          <ShippingStartListAndInput
            key={this.state.renderIteration}
            operation={this.operation}
            onSelect={(selected: ShippingSelectData, shipment: boolean) =>
              shipment ? this.onSelectAsn(selected) : this.onSelectParcel(selected)
            }
            onClickReadParcel={this.onReadParcel}
            onCreate={(
              formData: FormSchemaData,
              operation: ShippingOperationConfig,
              schema: FormSchema,
              shipment: boolean
            ) =>
              shipment ? this.onCreateAsn(formData, operation, schema) : this.onSubmit(formData, operation, schema)
            }
            shipment={!isParcel || defaultFilter.tab === 'shipment'}
            onDelete={this.handleDelete}
            canDelete={this.canDeleteOrClear}
            defaultFilter={defaultFilter}
            onFiltersChange={this.onFiltersChange}
          />
        )
      } else if (this.operation.canCreateShipments || this.operation.canCreateParcels) {
        return (
          <Page title={this.operation.description} onBackPress={() => Router.navigate('/')} enableEmulation={false}>
            <ShippingStartInput
              operation={this.operation}
              onCreate={isParcel ? this.onSubmit : this.onCreateAsn}
              shipment={!isParcel}
            />
          </Page>
        )
      }
    }

    const onBackPress =
      this.isAsnSelectMode() && this.operation.shippingMode === 'shipment' ? this.setSkipAsnSelection : undefined
    return (
      <>
        <OperationStart
          key={this.state.renderIteration}
          onBackPress={onBackPress}
          startWithRfidSearch={this.operation.startWithRfidSearch}
          submitPath={this.submitPath}
          extensions={ShippingExtensions}
          onSubmit={this.onSubmit}
          setOnDecodedItemCallback={this.operation.startWithRfidSearch ? this.setRfidReaderDecode : undefined}
        />
        {this.state.loading && <FullLoadingLayer />}
      </>
    )
  }
}
